import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Coins } from 'lucide-react';
import { isRTL } from '../utils/translations';

const currencyDetails = {
    USD: { symbol: '$', name: 'US Dollar', nameAr: 'دولار أمريكي' },
    EUR: { symbol: '€', name: 'Euro', nameAr: 'يورو' },
    GBP: { symbol: '£', name: 'British Pound', nameAr: 'جنيه إسترليني' },
    SAR: { symbol: 'ر.س', name: 'Saudi Riyal', nameAr: 'ريال سعودي' },
    AED: { symbol: 'د.إ', name: 'UAE Dirham', nameAr: 'درهم إماراتي' },
    KWD: { symbol: 'د.ك', name: 'Kuwaiti Dinar', nameAr: 'دينار كويتي' },
    QAR: { symbol: 'ر.ق', name: 'Qatari Riyal', nameAr: 'ريال قطري' },
    BHD: { symbol: 'د.ب', name: 'Bahraini Dinar', nameAr: 'دينار بحريني' },
    OMR: { symbol: 'ر.ع.', name: 'Omani Rial', nameAr: 'ريال عماني' },
    JOD: { symbol: 'د.ا', name: 'Jordanian Dinar', nameAr: 'دينار أردني' },
    EGP: { symbol: 'ج.م', name: 'Egyptian Pound', nameAr: 'جنيه مصري' },
    MAD: { symbol: 'د.م.', name: 'Moroccan Dirham', nameAr: 'درهم مغربي' },
    DZD: { symbol: 'د.ج', name: 'Algerian Dinar', nameAr: 'دينار جزائري' },
    TND: { symbol: 'د.ت', name: 'Tunisian Dinar', nameAr: 'دينار تونسي' },
    LYD: { symbol: 'د.ل', name: 'Libyan Dinar', nameAr: 'دينار ليبي' },
    IQD: { symbol: 'د.ع', name: 'Iraqi Dinar', nameAr: 'دينار عراقي' },
    LBP: { symbol: 'ل.ل', name: 'Lebanese Pound', nameAr: 'ليرة لبنانية' },
    MRU: { symbol: 'أ.م', name: 'Mauritanian Ouguiya', nameAr: 'أوقية موريتانية' },
    DJF: { symbol: 'ف.ج', name: 'Djiboutian Franc', nameAr: 'فرنك جيبوتي' },
    KMF: { symbol: 'ف.ق', name: 'Comorian Franc', nameAr: 'فرنك قمري' },
    YER: { symbol: 'ر.ي', name: 'Yemeni Rial', nameAr: 'ريال يمني' },
    SDG: { symbol: 'ج.س', name: 'Sudanese Pound', nameAr: 'جنيه سوداني' },
    SOS: { symbol: 'ش.ص', name: 'Somali Shilling', nameAr: 'شلن صومالي' },
    TRY: { symbol: '₺', name: 'Turkish Lira', nameAr: 'ليرة تركية' },
    AUD: { symbol: 'A$', name: 'Australian Dollar', nameAr: 'دولار أسترالي' },
    CAD: { symbol: 'C$', name: 'Canadian Dollar', nameAr: 'دولار كندي' },
    CHF: { symbol: 'CHF', name: 'Swiss Franc', nameAr: 'فرنك سويسري' },
    JPY: { symbol: '¥', name: 'Japanese Yen', nameAr: 'ين ياباني' },
    CNY: { symbol: '¥', name: 'Chinese Yuan', nameAr: 'يوان صيني' }
};

const availableCurrencies = [
    'SAR', 'AED', 'KWD', 'QAR', 'BHD', 'OMR', 'USD', 'EGP', 'MAD', 'DZD', 'TND', 'LYD', 'IQD', 'JOD', 'LBP', 'MRU', 'DJF', 'KMF', 'YER', 'SDG', 'SOS',
    'EUR', 'GBP', 'TRY', 'AUD', 'CAD', 'CHF', 'JPY', 'CNY'
];

const CurrencySelector = () => {
    const [currentCurrency, setCurrentCurrency] = useState('USD');
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const listRefs = useRef([]);

    useEffect(() => {
        const updateCurrencyState = () => {
            const geoContextStr = localStorage.getItem('luxvono_geo_context');
            if (geoContextStr) {
                try {
                    const ctx = JSON.parse(geoContextStr);
                    if (ctx.currency) {
                        setCurrentCurrency(ctx.currency);
                    }
                } catch (e) {
                    console.error("Failed to parse geo context in CurrencySelector", e);
                }
            }
        };

        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        // Initialize state
        updateCurrencyState();

        // Listen for language or geoContext changes
        window.addEventListener('languageChange', updateCurrencyState);
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            window.removeEventListener('languageChange', updateCurrencyState);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Positioning algorithm to prevent viewport overflow
    useEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (!wrapperRef.current) return;
            const rect = wrapperRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const dropdownWidth = 220; // Premium width for readable text
            const margin = 12;
            
            const rtl = isRTL();
            const preferredLeftOffset = rtl ? 0 : (rect.width - dropdownWidth);
            
            const minLeftOffset = margin - rect.left;
            const maxLeftOffset = viewportWidth - rect.left - dropdownWidth - margin;
            
            let finalLeftOffset = preferredLeftOffset;
            if (maxLeftOffset >= minLeftOffset) {
                finalLeftOffset = Math.max(minLeftOffset, Math.min(preferredLeftOffset, maxLeftOffset));
            } else {
                finalLeftOffset = (viewportWidth - dropdownWidth) / 2 - rect.left;
            }
            
            setDropdownStyle({
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: `${finalLeftOffset}px`,
                width: `${dropdownWidth}px`
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    const handleSwitch = (currency) => {
        const saved = localStorage.getItem('luxvono_geo_context');
        const geoContext = saved ? JSON.parse(saved) : { country: 'US', language: 'en', currency: 'USD' };
        geoContext.currency = currency;
        localStorage.setItem('luxvono_geo_context', JSON.stringify(geoContext));
        
        setIsOpen(false);
        if (buttonRef.current) {
            buttonRef.current.focus();
        }
        
        // Reload page to apply new prices globally
        window.location.reload();
    };

    // Keyboard accessibility handlers
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            if (isOpen) {
                event.preventDefault();
                setIsOpen(false);
                if (buttonRef.current) buttonRef.current.focus();
            }
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!isOpen) {
                setIsOpen(true);
            } else {
                const activeIndex = availableCurrencies.indexOf(currentCurrency);
                const nextIndex = (activeIndex + 1) % availableCurrencies.length;
                listRefs.current[nextIndex]?.focus();
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                setIsOpen(true);
            } else {
                const activeIndex = availableCurrencies.indexOf(currentCurrency);
                const prevIndex = (activeIndex - 1 + availableCurrencies.length) % availableCurrencies.length;
                listRefs.current[prevIndex]?.focus();
            }
        } else if (event.key === 'Tab') {
            setTimeout(() => {
                if (wrapperRef.current && !wrapperRef.current.contains(document.activeElement)) {
                    setIsOpen(false);
                }
            }, 50);
        }
    };

    const rtl = isRTL();
    const activeDetails = currencyDetails[currentCurrency] || { symbol: '$', name: 'US Dollar', nameAr: 'دولار أمريكي' };
    const displayLabel = `${activeDetails.symbol} ${currentCurrency}`;

    return (
        <div 
            ref={wrapperRef} 
            className="lux-currency-selector-wrapper" 
            style={{ position: 'relative', display: 'inline-block', zIndex: 9999 }}
            onKeyDown={handleKeyDown}
        >
            <style>{`
                /* Premium Pill Button */
                button.lux-currency-btn {
                    display: flex !important;
                    align-items: center !important;
                    gap: 8px !important;
                    padding: 6px 14px !important;
                    border-radius: 9999px !important;
                    background: rgba(255, 255, 255, 0.65) !important;
                    border: 1px solid rgba(197, 168, 128, 0.25) !important;
                    cursor: pointer !important;
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
                    outline: none !important;
                    color: #1d1d1f !important;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
                    height: auto !important;
                    width: auto !important;
                    line-height: normal !important;
                    white-space: nowrap !important;
                }
                [dir="rtl"] button.lux-currency-btn {
                    font-family: 'Cairo', 'Noto Sans Arabic', sans-serif !important;
                }
                
                button.lux-currency-btn:hover,
                button.lux-currency-btn:focus-visible {
                    background: rgba(255, 255, 255, 0.9) !important;
                    border-color: rgba(197, 168, 128, 0.5) !important;
                    box-shadow: 0 4px 12px rgba(197, 168, 128, 0.1) !important;
                    transform: translateY(-0.5px) !important;
                }
                
                button.lux-currency-btn:focus-visible {
                    box-shadow: 0 0 0 3px rgba(197, 168, 128, 0.25) !important;
                }
                
                .lux-currency-btn-text {
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    letter-spacing: 0.5px !important;
                }
                
                /* Dropdown styling */
                .lux-currency-dropdown {
                    position: absolute !important;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(25px);
                    -webkit-backdrop-filter: blur(25px);
                    border: 1px solid rgba(197, 168, 128, 0.25);
                    border-radius: 14px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.02);
                    padding: 6px;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    z-index: 10000;
                    box-sizing: border-box;
                    max-height: 280px;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                    
                    /* Transition properties */
                    opacity: 0;
                    visibility: hidden;
                    transform: translateY(-8px) scale(0.96);
                    pointer-events: none;
                    transition: opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), 
                                transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), 
                                visibility 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    transform-origin: top center;
                }
                .lux-currency-dropdown.active {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0) scale(1);
                    pointer-events: auto;
                }
                
                /* Dark Mode styling */
                body.theme-dark button.lux-currency-btn {
                    background: rgba(22, 22, 23, 0.65) !important;
                    border-color: rgba(197, 168, 128, 0.2) !important;
                    color: #f5f5f7 !important;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3) !important;
                }
                body.theme-dark button.lux-currency-btn:hover {
                    background: rgba(33, 33, 36, 0.8) !important;
                    border-color: rgba(197, 168, 128, 0.45) !important;
                    box-shadow: 0 4px 12px rgba(197, 168, 128, 0.15) !important;
                }
                body.theme-dark .lux-currency-dropdown {
                    background: rgba(18, 18, 18, 0.85);
                    border-color: rgba(197, 168, 128, 0.18);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 1px 5px rgba(0, 0, 0, 0.2);
                }
                
                /* Scrollbar inside dropdown */
                .lux-currency-dropdown::-webkit-scrollbar {
                    width: 4px;
                }
                .lux-currency-dropdown::-webkit-scrollbar-track {
                    background: transparent;
                }
                .lux-currency-dropdown::-webkit-scrollbar-thumb {
                    background: rgba(197, 168, 128, 0.3);
                    border-radius: 10px;
                }
                .lux-currency-dropdown::-webkit-scrollbar-thumb:hover {
                    background: rgba(197, 168, 128, 0.5);
                }
                
                /* Items */
                .lux-currency-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    border: none;
                    background: transparent;
                    border-radius: 10px;
                    cursor: pointer;
                    width: 100%;
                    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                    color: #515154;
                    outline: none;
                    box-sizing: border-box;
                    text-align: left;
                }
                body.theme-dark .lux-currency-item {
                    color: #a1a1a6;
                }
                .lux-currency-item:hover, .lux-currency-item:focus-visible {
                    background: rgba(197, 168, 128, 0.08);
                    color: #C5A880;
                }
                .lux-currency-item.active {
                    background: rgba(197, 168, 128, 0.12);
                    color: #C5A880;
                    font-weight: 600;
                }
                .lux-currency-item-left {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .lux-currency-symbol {
                    font-size: 13px;
                    font-weight: 700;
                    color: #C5A880;
                    min-width: 24px;
                    text-align: center;
                }
                .lux-currency-item-label {
                    font-size: 13px;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }
                [dir="rtl"] .lux-currency-item-label {
                    font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                }
                .lux-currency-check {
                    color: #C5A880;
                }
            `}</style>

            <button 
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)} 
                className="lux-currency-btn"
                aria-label="Select Currency"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <Coins size={14} style={{ color: '#C5A880' }} strokeWidth={1.8} />
                <span className="lux-currency-btn-text">{displayLabel}</span>
                <ChevronDown size={11} style={{ 
                    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: '#C5A880',
                    opacity: 0.8 
                }} />
            </button>
            
            <div 
                className={`lux-currency-dropdown ${isOpen ? 'active' : ''}`} 
                style={dropdownStyle} 
                role="listbox"
                aria-hidden={!isOpen}
            >
                {availableCurrencies.map((code, index) => {
                    const details = currencyDetails[code] || { symbol: '', name: code, nameAr: code };
                    const isSelected = currentCurrency === code;
                    const displayName = rtl ? details.nameAr : details.name;
                    
                    return (
                        <button 
                            key={code}
                            ref={el => listRefs.current[index] = el}
                            onClick={() => handleSwitch(code)}
                            className={`lux-currency-item ${isSelected ? 'active' : ''}`}
                            style={{ 
                                flexDirection: rtl ? 'row-reverse' : 'row',
                                textAlign: rtl ? 'right' : 'left'
                            }}
                            role="option"
                            aria-selected={isSelected}
                            tabIndex={isOpen ? 0 : -1}
                        >
                            <div className="lux-currency-item-left" style={{ flexDirection: rtl ? 'row-reverse' : 'row' }}>
                                <span className="lux-currency-symbol">{details.symbol}</span>
                                <span className="lux-currency-item-label">{code} - {displayName}</span>
                            </div>
                            {isSelected && <Check size={13} className="lux-currency-check" strokeWidth={2.5} />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default CurrencySelector;
